import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';
import { FiTrash2, FiMinus, FiPlus } from 'react-icons/fi';
import { useState } from 'react';
import { couponAPI } from '../api/services';
import toast from 'react-hot-toast';
import Loading from '../components/common/Loading';
import './Cart.css';

export default function Cart() {
  const { user } = useAuth();
  const { cart, updateQuantity, removeItem, clearCart, loading } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="empty-state">
        <h2>Please login to view your cart</h2>
        <Link to="/login" className="btn btn-primary">Login</Link>
      </div>
    );
  }

  if (loading) return <Loading />;

  const items = cart?.items || [];
  const subtotal = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const discountAmount = discount ? (discount.discountType === 'PERCENTAGE' ? subtotal * discount.discountValue / 100 : discount.discountValue) : 0;
  const total = Math.max(0, subtotal - discountAmount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const res = await couponAPI.validate(couponCode, subtotal);
      setDiscount(res.data.data);
      toast.success('Coupon applied!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid coupon');
      setDiscount(null);
    }
  };

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <h2>Your cart is empty</h2>
        <p>Browse our collection and add items to your cart.</p>
        <Link to="/shop" className="btn btn-primary">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <h1>Shopping Cart ({items.length} items)</h1>
      <div className="cart-layout">
        <div className="cart-items">
          {items.map(item => (
            <div key={item.id} className="cart-item">
              <div className="ci-image">
                {item.productImage ? <img src={item.productImage} alt={item.productName} /> : <div className="ci-no-img">No Image</div>}
              </div>
              <div className="ci-details">
                <span className="ci-name">{item.productName}</span>
                <p className="ci-variant">{item.size && `Size: ${item.size}`} {item.color && `Color: ${item.color}`}</p>
                <p className="ci-price">₹{item.unitPrice}</p>
              </div>
              <div className="ci-quantity">
                <button onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={item.quantity <= 1}><FiMinus /></button>
                <span>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.id, item.quantity + 1)}><FiPlus /></button>
              </div>
              <p className="ci-total">₹{(item.unitPrice * item.quantity).toFixed(2)}</p>
              <button className="ci-remove" onClick={() => removeItem(item.id)}><FiTrash2 /></button>
            </div>
          ))}
          <button className="clear-cart-btn" onClick={clearCart}><FiTrash2 /> Clear Cart</button>
        </div>

        <div className="cart-summary">
          <h3>Order Summary</h3>
          <div className="cs-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
          {discount && <div className="cs-row discount"><span>Discount</span><span>-₹{discountAmount.toFixed(2)}</span></div>}
          <div className="cs-row"><span>Shipping</span><span>Free</span></div>
          <hr />
          <div className="cs-row total"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
          <div className="coupon-row">
            <input type="text" placeholder="Coupon code" value={couponCode} onChange={e => setCouponCode(e.target.value)} />
            <button className="btn btn-outline btn-sm" onClick={handleApplyCoupon}>Apply</button>
          </div>
          <button className="btn btn-primary btn-block" onClick={() => navigate('/checkout', { state: { couponCode: discount ? couponCode : null } })}>
            Proceed to Checkout
          </button>
        </div>
      </div>
    </div>
  );
}
